import React from 'react';
import { Link } from 'react-router-dom';
import { QrCode, Zap, Palette, BarChart3, Shield, Globe, Wifi, User, MessageSquare, Mail, Download, Image, Lock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { SEOHead, faqSchema } from '../components/seo/SEOHead';

export const HomePage: React.FC = () => {
  const featureCards = [
    {
      icon: <QrCode className="h-12 w-12" />,
      title: "Why Use Our Free QR Code Generator?",
      description: "Our QR code generator is the best free tool for creating high-quality QR codes online. Whether you need a QR code for your business card, WiFi network, website URL, or marketing materials, we provide all the features you need without any registration or payment.",
      features: [
        "No registration required",
        "Unlimited QR code generation",
        "Professional quality output",
        "Works for all use cases"
      ],
      gradient: "from-green-500 to-emerald-600",
      bgGradient: "from-green-50 to-emerald-50"
    },
    {
      icon: <Download className="h-12 w-12" />,
      title: "Download Free QR Codes in Multiple Formats",
      description: "We support both PNG and SVG download formats. PNG is perfect for immediate use in documents, presentations, and social media. SVG provides vector graphics that scale infinitely without losing quality - ideal for professional printing and large format displays.",
      features: [
        "PNG format for digital use",
        "SVG vector graphics",
        "High-resolution printing",
        "Transparent backgrounds"
      ],
      gradient: "from-blue-500 to-cyan-600",
      bgGradient: "from-blue-50 to-cyan-50"
    },
    {
      icon: <Image className="h-12 w-12" />,
      title: "Add Your Logo to QR Codes",
      description: "Make your QR codes stand out by adding your company logo. Our smart logo scaling ensures your QR code remains scannable while looking professional. We automatically use high error correction levels when logos are added.",
      features: [
        "Smart logo scaling",
        "Professional appearance",
        "Automatic error correction",
        "Brand consistency"
      ],
      gradient: "from-purple-500 to-violet-600",
      bgGradient: "from-purple-50 to-violet-50"
    },
    {
      icon: <Lock className="h-12 w-12" />,
      title: "Privacy-First QR Code Creation",
      description: "Your privacy matters. All QR code generation happens locally in your browser. We never store, track, or have access to your QR code content. Create QR codes with complete confidence in your data security.",
      features: [
        "Local browser processing",
        "No data storage",
        "Complete privacy",
        "Secure generation"
      ],
      gradient: "from-orange-500 to-red-600",
      bgGradient: "from-orange-50 to-red-50"
    }
  ];

  const features = [
    {
      icon: <QrCode className="h-8 w-8" />,
      title: "Free QR Code Generation",
      description: "Create unlimited QR codes for URLs, text, emails, phone numbers, SMS, WiFi networks, vCards, and calendar events."
    },
    {
      icon: <Palette className="h-8 w-8" />,
      title: "Customizable Design",
      description: "Personalize your QR codes with custom colors, sizes, error correction levels, transparent backgrounds, and logo uploads."
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Instant Download",
      description: "Download QR codes as PNG or SVG instantly. No waiting, no registration required."
    },
    {
      icon: <BarChart3 className="h-8 w-8" />,
      title: "Shareable Links",
      description: "Generate shareable links with encoded parameters to recreate your QR codes anytime."
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Privacy-First",
      description: "Your data stays in your browser. We don't store or track your QR code content. Completely secure and private."
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: "Multiple Formats",
      description: "Download as PNG (with transparent option) or SVG (vector graphics). Perfect for print, web, and mobile."
    }
  ];

  const qrTypes = [
    { type: 'WiFi', icon: <Wifi className="h-8 w-8" />, desc: 'WiFi Networks', link: '/wifi-qr-code-generator' },
    { type: 'vCard', icon: <User className="h-8 w-8" />, desc: 'Contact Cards', link: '/vcard-qr-code-generator' },
    { type: 'SMS', icon: <MessageSquare className="h-8 w-8" />, desc: 'Text Messages', link: '/sms-qr-code-generator' },
    { type: 'Email', icon: <Mail className="h-8 w-8" />, desc: 'Email Addresses', link: '/email-qr-code-generator' },
    { type: 'URL', icon: '🌐', desc: 'Website Links', link: '/generate' },
    { type: 'Text', icon: '📝', desc: 'Plain Text', link: '/generate' },
  ];

  return (
    <>
      <SEOHead
        title="Free QR Code Generator — Create QR Codes Instantly"
        description="Create free QR codes for URLs, WiFi, vCard, SMS, email, and more. Download as PNG or SVG. Add logos and customize colors. No login required. Fast, secure, privacy-first QR code generator."
        keywords="free qr code, qr code generator, create qr code, qr code maker, online qr generator, wifi qr code, vcard qr code, sms qr code, email qr code"
        canonical="https://freeqrcodenow.com/"
        structuredData={faqSchema}
      />

      <div className="space-y-16">
        {/* Hero Section */}
        <section className="text-center py-16">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl font-bold gradient-text mb-6">
              Free QR Code Generator
            </h1>
            <h2 className="text-3xl font-semibold gradient-text mb-6">
              Create QR Codes Instantly
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Generate professional QR codes with logos, custom colors, and advanced features. 
              Download as PNG or SVG. No registration required - start creating free QR codes now!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/generate">
                <Button size="lg" className="w-full sm:w-auto btn-gradient">
                  <QrCode className="h-5 w-5 mr-2" />
                  Generate QR Code Free
                </Button>
              </Link>
              <a href="#qr-types">
                <Button variant="outline" size="lg" className="w-full sm:w-auto">
                  Explore QR Types
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold gradient-text mb-4">
              Everything You Need for Free QR Code Generation
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From simple text to complex WiFi networks, we've got you covered with powerful features.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-xl hover:scale-105 transition-all duration-300 border-2 hover:border-green-200">
                <CardHeader>
                  <div className="mx-auto mb-4 text-green-600">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl font-bold">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* QR Types Section */}
        <section id="qr-types" className="bg-gradient-to-br from-green-50 via-teal-50 to-blue-50 rounded-2xl p-8 shadow-lg">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold gradient-text mb-4">
              Create QR Codes Online for Multiple Types
            </h2>
            <p className="text-lg text-gray-600">
              Support for various content types to meet all your QR code needs.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {qrTypes.map((item, index) => (
              <Link key={index} to={item.link}>
                <div className="text-center p-6 bg-white rounded-xl hover:shadow-xl hover:scale-110 transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-green-300">
                  <div className="text-3xl mb-2 text-green-600">
                    {typeof item.icon === 'string' ? item.icon : item.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900">{item.type}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="gradient-primary rounded-2xl p-12 text-white shadow-2xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">
              How to Create Free QR Codes
            </h2>
            <p className="text-xl opacity-95">
              Generate professional QR codes in just 3 simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
              <div className="w-16 h-16 bg-white text-green-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2">Choose Type</h3>
              <p className="opacity-95">Select from URL, WiFi, vCard, SMS, Email, or Text</p>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
              <div className="w-16 h-16 bg-white text-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2">Customize</h3>
              <p className="opacity-95">Add colors, logos, and adjust size to match your brand</p>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
              <div className="w-16 h-16 bg-white text-green-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2">Download</h3>
              <p className="opacity-95">Download as PNG or SVG and use anywhere</p>
            </div>
          </div>
        </section>

        {/* Why Choose Us Cards Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold gradient-text mb-4">
                Why Choose Our QR Code Generator?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover the features that make us the best free QR code generator online
              </p>
            </div>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              {featureCards.map((card, index) => (
                <Card key={index} className={`relative overflow-hidden hover:shadow-2xl hover:scale-105 transition-all duration-500 border-0 bg-gradient-to-br ${card.bgGradient} group`}>
                  {/* Gradient accent */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

                  <CardContent className="relative p-8">
                    {/* Icon and Title */}
                    <div className="flex items-start space-x-6 mb-6">
                      <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-br ${card.gradient} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        {card.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-gray-800 transition-colors">
                          {card.title}
                        </h3>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-lg text-gray-700 leading-relaxed mb-6 group-hover:text-gray-800 transition-colors">
                      {card.description}
                    </p>

                    {/* Features List */}
                    <div className="space-y-3">
                      {card.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-3">
                          <div className={`w-2 h-2 bg-gradient-to-r ${card.gradient} rounded-full flex-shrink-0`}></div>
                          <span className="text-gray-700 group-hover:text-gray-800 transition-colors font-medium">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Hover effect overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none`}></div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Additional Benefits Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="text-center hover:shadow-xl hover:scale-105 transition-all duration-300 border-2 hover:border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 group">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Zap className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 group-hover:text-green-800 transition-colors">Lightning Fast</h3>
                  <p className="text-gray-600 group-hover:text-gray-700 transition-colors">Generate QR codes instantly with our optimized browser-based processing.</p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-xl hover:scale-105 transition-all duration-300 border-2 hover:border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 group">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Globe className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 group-hover:text-blue-800 transition-colors">Always Available</h3>
                  <p className="text-gray-600 group-hover:text-gray-700 transition-colors">Access our generator 24/7 from any device with an internet connection.</p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-xl hover:scale-105 transition-all duration-300 border-2 hover:border-purple-200 bg-gradient-to-br from-purple-50 to-violet-50 group">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 group-hover:text-purple-800 transition-colors">100% Secure</h3>
                  <p className="text-gray-600 group-hover:text-gray-700 transition-colors">Your data never leaves your browser. Complete privacy and security guaranteed.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center py-16 bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold gradient-text mb-4">
              Ready to Create Your Free QR Code?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Join thousands of users who trust FreeQRCodeNow for their QR code needs.
              Start generating professional QR codes today!
            </p>
            <Link to="/generate">
              <Button size="lg" className="btn-gradient">
                <QrCode className="h-5 w-5 mr-2" />
                Start Creating Free QR Codes Now
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};
